import { useState, type ReactNode, type ComponentType } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Database,
  Gauge,
  Github,
  Globe,
  Loader,
  Terminal,
  Wrench,
} from 'lucide-react';

import { RowanListCard } from './tools/RowanListCard';
import { RowanDetailCard } from './tools/RowanDetailCard';
import { GitHubCard } from './tools/GitHubCard';
import { WebSearchCard } from './tools/WebSearchCard';
import { WebFetchCard } from './tools/WebFetchCard';
import { EcosystemCard } from './tools/EcosystemCard';
import { CodeResultCard } from './tools/CodeResultCard';
import { GenericToolCard } from './tools/GenericToolCard';

export interface ToolRendererProps {
  toolName: string;
  result: unknown;
}

interface ToolCardProps {
  toolName: string;
  result: unknown;
  isStreaming?: boolean;
}

// TODO (Phase 5): replace regex dispatch with an explicit prefix map for
// speed and auditability. Current order-of-match is: Rowan list → Rowan
// detail → GitHub → Web → Prime/ecosystem → Code → Generic.
function getRenderer(toolName: string): ComponentType<ToolRendererProps> {
  if (/^(list_memories|list_goals|list_projects|list_tasks|list_kpis|list_observations|list_decisions|list_lessons|search_tasks|search_decisions)/.test(toolName)) {
    return RowanListCard;
  }
  if (/^(get_strategic_context|get_vision|get_goal|get_project|get_task|get_observation|get_kpi|get_recent_activity)/.test(toolName)) {
    return RowanDetailCard;
  }
  if (toolName.startsWith('github_')) return GitHubCard;
  if (toolName === 'web_search') return WebSearchCard;
  if (toolName === 'web_fetch') return WebFetchCard;
  if (toolName.startsWith('prime_')) return EcosystemCard;
  if (toolName === 'code_execution' || toolName === 'bash') return CodeResultCard;
  return GenericToolCard;
}

function iconForTool(toolName: string) {
  if (toolName.startsWith('github_')) return Github;
  if (toolName === 'web_search' || toolName === 'web_fetch') return Globe;
  if (toolName.startsWith('prime_')) return Gauge;
  if (toolName === 'code_execution' || toolName === 'bash') return Terminal;
  if (/^(list_|get_|search_)/.test(toolName)) return Database;
  return Wrench;
}

interface CollapsibleCardProps {
  toolName: string;
  children: ReactNode;
  defaultOpen?: boolean;
}

/**
 * Shared collapsible shell for all tool cards. Collapsed by default; header
 * shows tool name + chevron. Every card renderer should wrap its content
 * with this to keep the chat UI scannable.
 */
export function CollapsibleCard({
  toolName,
  children,
  defaultOpen = false,
}: CollapsibleCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = iconForTool(toolName);

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-fog bg-white/70">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-fog/30"
      >
        <Icon className="h-3.5 w-3.5 shrink-0 text-bark" aria-hidden />
        <span className="flex-1 truncate text-xs font-medium text-bark">
          {toolName}
        </span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-bark/60" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-bark/60" aria-hidden />
        )}
      </button>
      {open && (
        <div className="border-t border-fog px-3 py-2 text-xs text-espresso">
          {children}
        </div>
      )}
    </div>
  );
}

export function ToolCard({ toolName, result, isStreaming }: ToolCardProps) {
  if (isStreaming) {
    const Icon = iconForTool(toolName);
    return (
      <div className="my-2 flex items-center gap-2 rounded-lg border border-fog bg-white/70 px-3 py-2">
        <Icon className="h-3.5 w-3.5 text-bark" aria-hidden />
        <span className="flex-1 truncate text-xs font-medium text-bark">
          {toolName}
        </span>
        <Loader className="h-3.5 w-3.5 animate-spin text-gold" aria-hidden />
      </div>
    );
  }

  const Renderer = getRenderer(toolName);
  return <Renderer toolName={toolName} result={result} />;
}
